import React, { useRef } from 'react';
import { SketchPicker, ColorResult } from 'react-color';
import PopoverPortal from '../../../components/ui/popover-portal';
import { Label } from '../../../../components/ui/label';
import { FormSelect } from '../../../components/ui/form-select';
import type { AppearanceTabProps } from './types';
import { Orientation } from './types';

export default function AppearanceTab(props: AppearanceTabProps) {
  const {
    color,
    setColor,
    sketchPickerColor,
    setSketchPickerColor,
    displayColorPicker,
    setDisplayColorPicker,
    handleFieldFocus,
    isFieldInvalid,
    invalidFields,
    setInvalidFields,
    elementType,
    orientation = 'RIGHT',
    setOrientation,
    sticky = null,
    setSticky,
  } = props;

  const colorSwatchRef = useRef<HTMLButtonElement>(null);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let newColor = e.target.value;
    if (newColor && !newColor.startsWith('#')) {
      newColor = `#${newColor}`;
    }
    setColor(newColor);
  };

  // Render element-specific appearance options
  const renderElementOptions = () => {
    switch (elementType) {
      case 'add-to-cart':
        return (
          <div className="space-y-1">
            <Label className="font-inter text-base font-medium leading-6 text-ink-secondary">
              Placement Options
            </Label>
            <div className="flex gap-6">
              <button
                type="button"
                className="flex items-center gap-1"
                onClick={() => {
                  if (setSticky) {
                    setSticky(false);
                    handleFieldFocus('appearance-sticky');
                  }
                }}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                    isFieldInvalid('sticky')
                      ? 'border-red-500'
                      : sticky === false ? 'border-sage' : 'border-edge'
                  }`}
                  aria-hidden
                >
                  {sticky === false && (
                    <span className="h-2 w-2 rounded-full bg-sage" />
                  )}
                </span>
                <Label className="text-sm font-normal text-ink-secondary">
                  Stays on Top of Page
                </Label>
              </button>
              <button
                type="button"
                className="flex items-center gap-1"
                onClick={() => {
                  if (setSticky) {
                    setSticky(true);
                    handleFieldFocus('appearance-sticky');
                  }
                }}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                    isFieldInvalid('sticky')
                      ? 'border-red-500'
                      : sticky === true ? 'border-sage' : 'border-edge'
                  }`}
                  aria-hidden
                >
                  {sticky === true && (
                    <span className="h-2 w-2 rounded-full bg-sage" />
                  )}
                </span>
                <Label className="text-sm font-normal text-ink-secondary">
                  Follows on Scroll
                </Label>
              </button>
            </div>
          </div>
        );
      case 'fundraising-flag':
        return (
          <div className="space-y-0">
            <Label
              htmlFor="orientation"
              className="font-inter text-base font-medium leading-6 text-ink-secondary"
            >
              Flag Position
            </Label>
            <FormSelect
              id="orientation"
              value={orientation}
              onChange={(e) => {
                if (setOrientation) {
                  setOrientation(e.target.value as Orientation);
                  handleFieldFocus('appearance-orientation');
                }
              }}
              onFocus={() => handleFieldFocus('appearance-orientation')}
              className={`h-auto min-h-[28px] flex-shrink-0 self-stretch py-1 focus:ring-1 focus:ring-accent ${
                isFieldInvalid('orientation') ? 'border-red-500' : ''
              }`}
            >
              <option value="TOP">Top</option>
              <option value="BOTTOM">Bottom</option>
              <option value="LEFT">Left</option>
              <option value="RIGHT">Right</option>
            </FormSelect>
          </div>
        );
      case 'fundraise-bar':
      case 'qr-code':
        // These element types only have color picker, no additional options
        return null;

      default:
        return null;
    }
  };

  const handleClick = (): void => {
    setDisplayColorPicker(!displayColorPicker);
  };

  const handleClose = (): void => {
    setDisplayColorPicker(false);
  };

  return (
    <div className="space-y-6">
      {/* Color Picker Section */}
      <div className="space-y-1">
        <Label
          htmlFor="color"
          className="font-inter text-base font-medium leading-6 text-ink-secondary"
        >
          Color
        </Label>
        <div className="relative">
          <div className="flex gap-2">
            <button
              type="button"
              ref={colorSwatchRef}
              className={`h-[28px] w-[54px] cursor-pointer rounded-md border ${
                isFieldInvalid('color') ? 'border-red-500' : 'border-edge'
              } bg-white`}
              onClick={handleClick}
              onFocus={() => handleFieldFocus('appearance-color')}
            >
              <div
                className="h-full w-full rounded-md"
                style={{ backgroundColor: color || 'transparent' }}
              />
            </button>

            {displayColorPicker ? (
              <PopoverPortal anchorRef={colorSwatchRef} onClose={handleClose}>
                <SketchPicker
                  color={sketchPickerColor}
                  onChange={(colorResult: ColorResult) => {
                    setSketchPickerColor(colorResult.rgb);
                    setColor(colorResult.hex);
                    handleFieldFocus('appearance-color');
                  }}
                />
              </PopoverPortal>
            ) : null}

            <input
              value={color}
              placeholder="#FFFFFF"
              onChange={handleColorChange}
              onFocus={() => handleFieldFocus('appearance-color')}
              className={`h-[28px] flex-1 rounded-md border ${
                isFieldInvalid('color') ? 'border-red-500' : 'border-edge'
              } bg-white px-2`}
            />
          </div>
        </div>
      </div>

      {/* Element-specific options */}
      {renderElementOptions()}
    </div>
  );
}
